import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import { isEmpty, isString } from 'lodash';
import React, { useContext, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { FixedLimitStackParamList, FixedLimitStackRoutes } from '@/features/account-management/screens/fixed-limit/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface FixedLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const FixedLimitNativeOneScreen: React.FC<FixedLimitProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const { body, textTitle, textAtmPin, btnView, btn, modal, topHeader } = sxStyles;
  const [loading, setLoading] = useState(false);

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<FixedLimitStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');

  const snapPoints = useMemo(() => ['50%', '90%'], []);
  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet enablePanDownToClose index={1} snapPoints={snapPoints} backdropComponent={renderBackdrop}>
        <ScrollView sx={{ backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
          <View sx={modal}>
            <View sx={topHeader}>
              <BottomSheetHeader
                title={t('card-account-management::fixed-limit.confirmation-title--fixed-limit-confirmation')}
                onPress={() => navigation.popToTop()}
              />
            </View>
            <View sx={body}>
              <TextBody semiBold>{t('card-account-management::fixed-limit.confirmation-text--are-you-sure-to-fixed-limit')}</TextBody>
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account-management::fixed-limit.form-text--limit')}
                </TextCaption>
                <H6 sx={textAtmPin}>
                  {formatAmountByDecimal({ value: data.limit, decimals: 2 })} {record.currency}
                </H6>
              </View>
              {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
              <View sx={btnView}>
                <UniButton
                  sx={btn}
                  loading={loading}
                  labelSx={{ color: '$balck' }}
                  label={t('card-account-management::general.button--place-instruction')}
                  onPress={() => {
                    if (func) {
                      setLoading(true);
                      func(
                        data.limit,
                        (returnData) => {
                          setLoading(false);
                          onReturnData && onReturnData(returnData.data);
                          navigation.navigate(FixedLimitStackRoutes.Placed, {
                            record,
                            onLayout: onLayout,
                            data,
                            returnData: returnData.data,
                            refresh
                          });
                        },
                        (error) => {
                          setLoading(false);
                          const data: any = error.response?.data;
                          const errorMessage =
                            data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                          setAlertMessage(errorMessage);
                        }
                      );
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {
    gap: '$5'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
